import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ListTodo, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { PRIORITIES, type BulkOp, type Priority } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/shared/EmptyState';
import { TaskRow, type TaskRowTimer } from '@/components/shared/TaskRow';
import { ApiError } from '@/lib/api';
import { useUsers } from '@/features/settings/api';
import { useTaskRowTimer } from '@/features/time/useTaskRowTimer';
import { useProject } from '@/features/projects';
import { FilterBar, useFilters } from './FilterBar';
import { useBulkTasks, useTasks, type TaskGroupView, type TaskView } from './api';

const VIRTUALISE_ABOVE = 100;

function isGrouped(items: TaskView[] | TaskGroupView[]): items is TaskGroupView[] {
  return items.length > 0 && 'groupKey' in items[0];
}

function BulkBar({
  selected,
  statuses,
  onDone,
}: {
  selected: string[];
  statuses: { id: string; name: string }[];
  onDone: () => void;
}) {
  const bulk = useBulkTasks();

  async function run(op: BulkOp, payload: Record<string, unknown>, description: string) {
    try {
      const result = await bulk.mutateAsync({ taskIds: selected, op, payload });
      if (result.skipped.length === 0) {
        toast.success(`${result.updated.length} ${description}`);
      } else {
        const forbidden = result.skipped.filter((s) => s.reason === 'FORBIDDEN').length;
        toast.warning(
          `${result.updated.length} updated, ${result.skipped.length} skipped`,
          { description: forbidden ? `${forbidden} you do not have permission to edit.` : undefined },
        );
      }
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Bulk action failed');
    }
  }

  return (
    <div
      className="sticky bottom-3 z-30 mx-auto flex w-fit items-center gap-2 rounded-[8px] border border-[var(--border)] bg-[var(--surface)] px-3 py-2 shadow-[var(--elevation)]"
      role="region"
      aria-label="Bulk actions"
    >
      <span className="text-[13px] font-medium">
        {selected.length} selected
      </span>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary" size="sm">
            Status
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          <DropdownMenuLabel>Move to</DropdownMenuLabel>
          {statuses.map((s) => (
            <DropdownMenuItem key={s.id} onSelect={() => void run('status', { statusId: s.id }, 'moved')}>
              {s.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary" size="sm">
            Priority
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          {PRIORITIES.map((p: Priority) => (
            <DropdownMenuItem key={p} onSelect={() => void run('priority', { priority: p }, 'reprioritised')}>
              {p}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary" size="sm">
            More
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          <DropdownMenuItem onSelect={() => void run('archive', {}, 'archived')}>Archive</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem destructive onSelect={() => void run('delete', {}, 'deleted')}>
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {bulk.isPending && <Loader2 className="size-4 animate-spin" />}
      <Button variant="ghost" size="sm" onClick={onDone}>
        Cancel
      </Button>
    </div>
  );
}

function VirtualTaskList({
  tasks,
  statusNames,
  people,
  selected,
  timerFor,
  onSelect,
  onOpen,
}: {
  tasks: TaskView[];
  statusNames: Record<string, string>;
  people: Record<string, { name: string; avatarUrl?: string }>;
  selected: Set<string>;
  timerFor: (taskId: string) => TaskRowTimer;
  onSelect: (id: string, checked: boolean) => void;
  onOpen: (task: TaskView) => void;
}) {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualiser = useVirtualizer({
    count: tasks.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 12,
  });

  // Below the threshold the browser handles it fine and plain DOM is simpler.
  if (tasks.length <= VIRTUALISE_ABOVE) {
    return (
      <div role="rowgroup">
        {tasks.map((task) => (
          <TaskRow
            key={task._id}
            task={task}
            statusName={statusNames[task.statusId]}
            people={people}
            selected={selected.has(task._id)}
            timer={timerFor(task._id)}
            onSelect={(checked) => onSelect(task._id, checked)}
            onOpen={() => onOpen(task)}
          />
        ))}
      </div>
    );
  }

  return (
    <div ref={parentRef} className="max-h-[calc(100dvh-200px)] overflow-auto" role="rowgroup">
      <div style={{ height: virtualiser.getTotalSize(), position: 'relative' }}>
        {virtualiser.getVirtualItems().map((item) => {
          const task = tasks[item.index];
          return (
            <div
              key={task._id}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${item.start}px)`,
              }}
              ref={virtualiser.measureElement}
              data-index={item.index}
            >
              <TaskRow
                task={task}
                statusName={statusNames[task.statusId]}
                people={people}
                selected={selected.has(task._id)}
                timer={timerFor(task._id)}
                onSelect={(checked) => onSelect(task._id, checked)}
                onOpen={() => onOpen(task)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function TaskListView({ scope = 'project' }: { scope?: 'project' | 'mine' }) {
  const { key } = useParams();
  const navigate = useNavigate();
  const filters = useFilters();
  const { data: project } = useProject(scope === 'project' ? key : undefined);
  const { data: people = [] } = useUsers();
  const timerFor = useTaskRowTimer();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const query = useMemo(() => {
    const base = filters.toQuery();
    if (scope === 'project' && project) base.projectId = project._id;
    if (scope === 'mine') base.assigneeId = base.assigneeId ?? 'me';
    return { ...base, limit: 200 };
  }, [filters, project, scope]);

  const { data, isLoading } = useTasks(query, {
    enabled: scope === 'mine' || Boolean(project),
  });

  const peopleById = useMemo(
    () => Object.fromEntries(people.map((p) => [p._id, { name: p.name, avatarUrl: p.avatarUrl }])),
    [people],
  );
  const statusNames = useMemo(
    () => Object.fromEntries((project?.statuses ?? []).map((s) => [s.id, s.name])),
    [project],
  );

  function toggleSelect(id: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  const openTask = (task: TaskView) => navigate(`/tasks/${task.key}`);
  const items = data?.items ?? [];
  const total = data?.meta?.total ?? 0;

  return (
    <>
      <PageHeader
        title={scope === 'mine' ? 'My Tasks' : `${project?.icon ?? ''} ${project?.name ?? 'List'}`}
        subtitle={`${total} ${total === 1 ? 'task' : 'tasks'}`}
        actions={
          scope === 'project' && project ? (
            <Button variant="primary" size="sm" onClick={() => navigate(`/projects/${project.key}/board`)}>
              <Plus />
              Board view
            </Button>
          ) : null
        }
        toolbar={<FilterBar />}
      />

      <div className="p-4">
        {isLoading ? (
          <div className="flex flex-col gap-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-9" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<ListTodo className="size-6" />}
            title={filters.activeCount > 0 ? 'Nothing matches those filters' : 'No tasks yet'}
            description={
              filters.activeCount > 0
                ? 'Loosen a filter, or clear them all to see everything.'
                : 'Press ⌘N to quick-add the first one.'
            }
            action={
              filters.activeCount > 0 ? (
                <Button variant="secondary" onClick={filters.clearAll}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : isGrouped(items) ? (
          <div className="flex flex-col gap-4">
            {items.map((group) => (
              <section key={group.groupKey} className="vt-surface overflow-hidden">
                <h2 className="sticky top-0 z-10 border-b border-[var(--border)] bg-[var(--surface-2)] px-3 py-1.5 text-[12px] font-medium">
                  {group.groupLabel}
                  <span className="ml-1.5 vt-muted">{group.count}</span>
                </h2>
                <VirtualTaskList
                  tasks={group.tasks}
                  statusNames={statusNames}
                  people={peopleById}
                  selected={selected}
                  timerFor={timerFor}
                  onSelect={toggleSelect}
                  onOpen={openTask}
                />
              </section>
            ))}
          </div>
        ) : (
          <div className="vt-surface overflow-hidden" role="table" aria-label="Tasks">
            <VirtualTaskList
              tasks={items}
              statusNames={statusNames}
              people={peopleById}
              selected={selected}
              timerFor={timerFor}
              onSelect={toggleSelect}
              onOpen={openTask}
            />
          </div>
        )}
      </div>

      {selected.size > 0 && (
        <BulkBar
          selected={[...selected]}
          statuses={project?.statuses ?? []}
          onDone={() => setSelected(new Set())}
        />
      )}
    </>
  );
}
